import * as React from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshotCount: number;
  snapshotName?: string;
  title?: string;
  description?: string;
  confirmLabel?: string;
  loadingLabel?: string;
  onConfirmDelete: () => void | Promise<void>;
  isDeleting?: boolean;
}

export const DeleteSnapshotsDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  snapshotCount,
  snapshotName,
  title,
  description,
  confirmLabel = "Delete",
  loadingLabel = "Deleting...",
  onConfirmDelete,
  isDeleting = false,
}) => {
  const handleDelete = async () => {
    await onConfirmDelete();
    onOpenChange(false);
  };

  const dialogTitle =
    title ??
    (snapshotCount === 1 && snapshotName
      ? `Delete "${snapshotName}"?`
      : `Delete ${snapshotCount} Snapshot${snapshotCount !== 1 ? "s" : ""}`);

  const dialogDescription =
    description ??
    (snapshotCount === 1
      ? "Are you sure you want to delete this snapshot? This action cannot be undone."
      : `Are you sure you want to delete ${snapshotCount} snapshot${snapshotCount !== 1 ? "s" : ""}? This action cannot be undone.`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" disabled={isDeleting}>
              Cancel
            </Button>
          </DialogClose>
          <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? (
              loadingLabel
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                {confirmLabel}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
